/*
 * TOTIS BASICS – Selector de packs (Pack 3 / Pack 6 / 1 unidad).
 *
 * Los datos (colores, tallas, variantes, stock, porcentajes) los pinta snippets/totis-packs.liquid en un JSON.
 * Cada unidad del pack tiene su color y su talla. Al añadir, todas las unidades van a la cesta en una sola llamada a
 * /cart/add.js con la propiedad oculta "_pack" para poder agruparlas.
 */
(() => {
  if (window.customElements.get('totis-packs')) {
    return;
  }

  const escapeHtml = (value) => String(value).replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);

  const formatMoney = (cents, format) => {
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = format.match(placeholder);

    const withDelimiters = (amount, precision, thousands, decimal) => {
      const [integer, fraction] = (amount / 100).toFixed(precision).split('.');
      const integerWithThousands = integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? `${integerWithThousands}${decimal}${fraction}` : integerWithThousands;
    };

    let value;
    switch (match ? match[1] : 'amount') {
      case 'amount_no_decimals': value = withDelimiters(cents, 0, ',', '.'); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'", '.'); break;
      default: value = withDelimiters(cents, 2, ',', '.');
    }

    return format.replace(placeholder, value);
  };

  class TotisPacks extends HTMLElement {
    connectedCallback() {
      if (this.data) {
        return;
      }

      this.data = JSON.parse(this.querySelector('script[data-totis-packs]').textContent);
      this.packSize = 3;
      this.sameSize = false;
      this.units = Array.from({ length: 6 }, () => ({ color: this.data.initialColor, size: null }));

      // Básicos primero y luego Temporada, cada grupo por su campo "Orden"
      this.sortedColors = [...this.data.colors].sort((a, b) => (a.group === b.group ? a.order - b.order : a.group === 'basic' ? -1 : 1));

      if (this.data.sizes.length === 0) {
        this.units.forEach((unit) => (unit.size = ''));
      }

      this.rowsElement = this.querySelector('[data-rows]');
      this.errorElement = this.querySelector('[data-error]');
      this.sameSizeWrapper = this.querySelector('[data-same-size-wrapper]');

      this.addEventListener('click', this.onClick.bind(this));
      this.querySelector('[data-same-size]').addEventListener('change', this.onSameSizeChange.bind(this));

      this.render();
      this.avoidPreviewBar();
    }

    // Solo en la vista previa del tema: la barra de Shopify ("Development… Draft") tapa el borde inferior. Apoyamos el
    // botón fijo justo encima de ella. Los clientes no tienen esa barra, así que para ellos va pegado abajo del todo.
    avoidPreviewBar() {
      const update = () => {
        const rect = document.getElementById('PBarNextFrameWrapper')?.getBoundingClientRect();
        const offset = rect && rect.height > 0 && rect.top < window.innerHeight ? Math.max(0, Math.round(window.innerHeight - rect.top)) : 0;
        this.style.setProperty('--totis-preview-bar-offset', `${offset}px`);
      };

      update();
      setTimeout(update, 1500);
      window.addEventListener('resize', update);
      new MutationObserver(update).observe(document.body, { childList: true });
    }

    /* ---------- Datos ---------- */

    findVariant(color, size) {
      const { colorIndex, sizeIndex, variants } = this.data;
      return variants.find((variant) => (colorIndex === -1 || variant.options[colorIndex] === color) && (sizeIndex === -1 || variant.options[sizeIndex] === size));
    }

    // Unidades de esta variante ya elegidas en las OTRAS filas del pack
    usedByOtherUnits(variantId, unitIndex) {
      return this.units.slice(0, this.packSize).filter((unit, index) => {
        if (index === unitIndex || unit.size === null) {
          return false;
        }
        return this.findVariant(unit.color, unit.size)?.id === variantId;
      }).length;
    }

    isAvailableFor(unitIndex, color, size) {
      const variant = this.findVariant(color, size);

      if (!variant || !variant.available) {
        return false;
      }

      return variant.stock === null || variant.stock - this.usedByOtherUnits(variant.id, unitIndex) > 0;
    }

    isColorAvailableFor(unitIndex, color) {
      if (this.data.sizes.length === 0) {
        return this.isAvailableFor(unitIndex, color, '');
      }
      return this.data.sizes.some((size) => this.isAvailableFor(unitIndex, color, size));
    }

    get activeUnits() {
      return this.units.slice(0, this.packSize);
    }

    get totals() {
      const discount = this.data.discounts[this.packSize] || 0;
      const fallbackPrice = this.data.variants[0].price;

      const compare = this.activeUnits.reduce((sum, unit) => {
        const variant = this.findVariant(unit.color, unit.size) || this.data.variants.find((item) => this.data.colorIndex === -1 || item.options[this.data.colorIndex] === unit.color);
        return sum + (variant ? variant.price : fallbackPrice);
      }, 0);

      return { compare, total: Math.round((compare * (100 - discount)) / 100) };
    }

    /* ---------- Eventos ---------- */

    onClick(event) {
      const packButton = event.target.closest('[data-pack-size]');
      const colorButton = event.target.closest('[data-unit-color]');
      const sizeButton = event.target.closest('[data-unit-size]');
      const addButton = event.target.closest('[data-add]');

      if (packButton) {
        this.selectPack(parseInt(packButton.dataset.packSize, 10));
      } else if (colorButton) {
        this.selectColor(parseInt(colorButton.dataset.unit, 10), colorButton.dataset.unitColor);
      } else if (sizeButton && sizeButton.getAttribute('aria-disabled') !== 'true') {
        this.selectSize(parseInt(sizeButton.dataset.unit, 10), sizeButton.dataset.unitSize);
      } else if (addButton) {
        this.addToCart();
      }
    }

    selectPack(size) {
      this.packSize = size;

      this.querySelectorAll('[data-pack-size]').forEach((button) => {
        const selected = parseInt(button.dataset.packSize, 10) === size;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-checked', selected ? 'true' : 'false');
      });

      // Las unidades nuevas toman la talla común si "Misma talla para todas" está marcado
      if (this.sameSize) {
        const commonSize = this.units[0].size;
        this.activeUnits.forEach((unit, index) => {
          if (commonSize !== null && this.isAvailableFor(index, unit.color, commonSize)) {
            unit.size = commonSize;
          }
        });
      }

      this.hideError();
      this.render();
    }

    selectColor(unitIndex, color) {
      const unit = this.units[unitIndex];
      unit.color = color;

      if (unit.size !== null && !this.isAvailableFor(unitIndex, color, unit.size)) {
        unit.size = this.data.sizes.length === 0 ? '' : null;
      }

      this.syncGallery(color);
      this.render();
    }

    selectSize(unitIndex, size) {
      if (this.sameSize) {
        this.activeUnits.forEach((unit, index) => {
          if (this.isAvailableFor(index, unit.color, size)) {
            unit.size = size;
          }
        });
      } else {
        this.units[unitIndex].size = size;
      }

      this.hideError();
      this.render();
    }

    onSameSizeChange(event) {
      this.sameSize = event.target.checked;

      const firstChosen = this.activeUnits.find((unit) => unit.size !== null);
      if (this.sameSize && firstChosen) {
        this.selectSize(0, firstChosen.size);
      }
    }

    // Al elegir un color en una fila, la foto grande cambia a ese color (usando el selector del tema, oculto)
    syncGallery(color) {
      const colorData = this.data.colors.find((item) => item.name === color);
      if (!colorData) {
        return;
      }

      const input = document.querySelector(`input[data-option-position][form="${this.data.formId}"][value="${colorData.optionValueId}"]`);
      if (input && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    /* ---------- Pintar ---------- */

    render() {
      this.sameSizeWrapper.hidden = this.packSize === 1 || this.data.sizes.length === 0;
      this.rowsElement.innerHTML = this.activeUnits.map((unit, index) => this.renderUnit(unit, index)).join('');
      this.renderButton();
    }

    renderUnit(unit, index) {
      const color = this.data.colors.find((item) => item.name === unit.color);
      const showNumber = this.packSize > 1;
      const summary = [color ? color.label : '', unit.size || ''].filter(Boolean).join(' · ');

      // Dos filas: básicos arriba y temporada debajo. Si un grupo no cabe en una línea, sigue en la siguiente (nada
      // queda escondido fuera de la pantalla)
      const colorGroups = { basic: '', seasonal: '' };

      this.sortedColors.forEach((item) => {
        const available = this.isColorAvailableFor(index, item.name);
        const selected = item.name === unit.color;
        const label = `${item.label}${item.tag ? ` (${item.tag})` : ''}${available ? '' : ' (agotado)'}`;

        colorGroups[item.group] += `<button type="button" class="totis-unit__swatch${selected ? ' is-selected' : ''}${available ? '' : ' is-disabled'}" data-unit="${index}" data-unit-color="${escapeHtml(item.name)}" aria-pressed="${selected}" aria-label="${escapeHtml(label)}" title="${escapeHtml(item.label)}" style="--swatch-background: ${escapeHtml(item.swatch)}">${item.tag ? '<span class="totis-swatch-tag" aria-hidden="true"></span>' : ''}</button>`;
      });

      const sizesHtml = this.data.sizes.map((size) => {
        const available = this.isAvailableFor(index, unit.color, size);
        const selected = size === unit.size;
        return `<button type="button" class="totis-unit__size${selected ? ' is-selected' : ''}${available ? '' : ' is-disabled'}" data-unit="${index}" data-unit-size="${escapeHtml(size)}" aria-pressed="${selected}" aria-disabled="${!available}">${escapeHtml(size)}${available ? '' : '<span class="sr-only"> (agotada)</span>'}</button>`;
      }).join('');

      const image = color && color.image ? `<img class="totis-unit__thumb" src="${color.image}" alt="" width="40" height="50" loading="lazy">` : '<span class="totis-unit__thumb" aria-hidden="true"></span>';

      const flagMissing = this.flagMissingSizes && unit.size === null;
      const hint = unit.size === null && this.data.sizes.length > 0 ? ` <span class="totis-unit__hint">${flagMissing ? '· Elige una talla' : '· elige talla'}</span>` : '';

      return `
        <div class="totis-unit${flagMissing ? ' is-missing-size' : ''}" data-unit-row="${index}">
          ${image}
          <div class="totis-unit__body">
            <p class="totis-unit__title">${showNumber ? `<strong>#${index + 1}</strong> · ` : ''}${escapeHtml(summary)}${hint}</p>
            <div class="totis-unit__colors" role="group" aria-label="Color${showNumber ? ` de la ${this.data.noun} ${index + 1}` : ''}">
              ${colorGroups.basic ? `<div class="totis-unit__color-group">${colorGroups.basic}</div>` : ''}
              ${colorGroups.seasonal ? `<div class="totis-unit__color-group">${colorGroups.seasonal}</div>` : ''}
            </div>
            ${sizesHtml ? `<div class="totis-unit__sizes" role="group" aria-label="Talla${showNumber ? ` de la ${this.data.noun} ${index + 1}` : ''}">${sizesHtml}</div>` : ''}
          </div>
        </div>`;
    }

    renderButton() {
      const { total } = this.totals;
      const what = this.packSize === 1 ? 'Añadir a la cesta' : `Añadir ${this.packSize} ${this.data.nounPlural}`;
      const text = `${what} · ${formatMoney(total, this.data.moneyFormat)}`;

      this.querySelectorAll('[data-add]').forEach((button) => {
        if (!button.hasAttribute('aria-busy')) {
          button.textContent = text;
        }
      });
    }

    showError(message) {
      this.errorElement.textContent = message;
      this.errorElement.hidden = false;
      this.errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    hideError() {
      this.errorElement.hidden = true;
    }

    /* ---------- Añadir a la cesta ---------- */

    async addToCart() {
      const missingIndex = this.activeUnits.findIndex((unit) => unit.size === null);

      if (missingIndex !== -1) {
        // Resaltamos en rojo las filas sin talla y llevamos al cliente a la primera
        this.flagMissingSizes = true;
        this.render();

        const row = this.rowsElement.querySelector(`[data-unit-row="${missingIndex}"]`);
        row?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        row?.classList.add('is-shaking');
        setTimeout(() => row?.classList.remove('is-shaking'), 600);
        return;
      }

      const quantities = new Map();
      for (const [index, unit] of this.activeUnits.entries()) {
        const variant = this.findVariant(unit.color, unit.size);

        if (!variant || !variant.available) {
          this.showError(`La ${this.data.noun} #${index + 1} está agotada en ese color y talla. Elige otra combinación.`);
          return;
        }

        quantities.set(variant.id, (quantities.get(variant.id) || 0) + 1);
      }

      for (const [variantId, quantity] of quantities) {
        const variant = this.data.variants.find((item) => item.id === variantId);
        if (variant.stock !== null && quantity > variant.stock) {
          this.showError('No hay stock suficiente para alguna de las combinaciones elegidas.');
          return;
        }
      }

      const packId = `P${this.packSize}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
      const items = Array.from(quantities, ([id, quantity]) => ({
        id,
        quantity,
        ...(this.packSize > 1 ? { properties: { _pack: packId } } : {})
      }));

      const buttons = this.querySelectorAll('[data-add]');
      buttons.forEach((button) => {
        button.setAttribute('aria-busy', 'true');
        button.disabled = true;
      });

      try {
        const response = await fetch(this.data.cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.description || errorData.message || 'No se ha podido añadir a la cesta.');
        }

        window.location.href = this.data.cartUrl;
      } catch (error) {
        this.showError(error.message);
        buttons.forEach((button) => {
          button.removeAttribute('aria-busy');
          button.disabled = false;
        });
        this.renderButton();
      }
    }
  }

  window.customElements.define('totis-packs', TotisPacks);
})();
